import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { TerritoryLabel } from '~/components/TerritoryLabel/TerritoryLabel';
import { formatPercent } from '~/modules/artist-roster/components/TrackDetails/components/StreamsTerritoryTable/utils';
import { formatCount } from '~/modules/artist-roster/lib/utils';

import type { CampaignBenchmark } from '../lib/types';
import { formatCurrency } from '../lib/utils';

export function useBenchmarkColumns() {
  const { t } = useTranslation('campaign-planner', { keyPrefix: 'benchmarks' });
  const { t: nameTranslation } = useTranslation('names');

  const columnHelper = useMemo(
    () => createColumnHelper<CampaignBenchmark>(),
    [],
  );

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('market', {
        id: 'market',
        meta: {
          className: 'w-[20%]',
        },
        header: () => <span className="text-subtle">{t('header.market')}</span>,
        cell: ({ row }) => (
          <span className="text-strong">
            <TerritoryLabel code={row.original.market} />
          </span>
        ),
      }),
      columnHelper.accessor('objective', {
        id: 'objective',
        header: () => (
          <span className="text-subtle">{t('header.objective')}</span>
        ),
        cell: ({ row }) => (
          <span className="text-strong">{row.original.objective}</span>
        ),
      }),
      columnHelper.accessor('platform', {
        id: 'platform',
        header: () => (
          <span className="text-subtle">{t('header.platform')}</span>
        ),
        cell: ({ row }) =>
          row.original.platform ? (
            <div className="flex items-center gap-sm">
              <Icon
                type={row.original.platform.toLowerCase()}
                variant="brands"
                size="md"
                aria-label={t('header.platform')}
              />
              <span className="text-strong">
                {nameTranslation(row.original.platform)}
              </span>
            </div>
          ) : (
            <span className="text-strong">--</span>
          ),
      }),
      columnHelper.accessor('platformFollowers', {
        id: 'platformFollowers',
        header: () => (
          <span className="flex justify-center text-subtle">
            {t('header.platformFollowers')}
          </span>
        ),
        cell: ({ row }) => (
          <span className="flex justify-center font-semibold">
            {formatCount(row.original.platformFollowers)}
          </span>
        ),
      }),
      columnHelper.accessor('CPM', {
        id: 'CPM',
        meta: {
          className: 'w-[6%]',
        },
        header: () => (
          <span className="flex justify-center text-subtle">
            {t('header.CPM')}
          </span>
        ),
        cell: ({ row }) => (
          <span className="flex justify-center font-semibold">
            {formatCurrency({ value: row.original.CPM })}
          </span>
        ),
      }),
      columnHelper.accessor('CPV', {
        id: 'CPV',
        meta: {
          className: 'w-[6%]',
        },
        header: () => (
          <span className="flex justify-center text-subtle">
            {t('header.CPV')}
          </span>
        ),
        cell: ({ row }) => (
          <span className="flex justify-center font-semibold">
            {formatCurrency({ value: row.original.CPV })}
          </span>
        ),
      }),

      columnHelper.accessor('CPC', {
        id: 'CPC',
        meta: {
          className: 'w-[6%]',
        },
        header: () => (
          <span className="flex justify-center text-subtle">
            {t('header.CPC')}
          </span>
        ),
        cell: ({ row }) => (
          <span className="flex justify-center font-semibold">
            {formatCurrency({ value: row.original.CPC })}
          </span>
        ),
      }),
      columnHelper.accessor('VTR', {
        id: 'VTR',
        meta: {
          className: 'w-[6%]',
        },
        header: () => (
          <span className="flex justify-center text-subtle">
            {t('header.VTR')}
          </span>
        ),
        cell: ({ row }) =>
          row.original.VTR ? (
            <span className="flex justify-center font-semibold">
              {formatPercent(row.original.VTR, 4)}
            </span>
          ) : (
            <span className="flex justify-center font-semibold">--</span>
          ),
      }),
      columnHelper.accessor('CTR', {
        id: 'CTR',
        meta: {
          className: 'w-[6%]',
        },
        header: () => (
          <span className="flex justify-center text-subtle">
            {t('header.CTR')}
          </span>
        ),
        cell: ({ row }) =>
          row.original.CTR ? (
            <span className="flex justify-center font-semibold">
              {formatPercent(row.original.CTR, 4)}
            </span>
          ) : (
            <span className="flex justify-center font-semibold">--</span>
          ),
      }),
      columnHelper.accessor('engagementRate', {
        id: 'engagementRate',
        header: () => (
          <span className="flex justify-center text-subtle">
            {t('header.engagementRate')}
          </span>
        ),
        cell: ({ row }) => (
          <div className="flex justify-center">
            {row.original.engagementRate ? (
              <span className="font-semibold">
                {formatPercent(row.original.engagementRate, 4)}
              </span>
            ) : (
              <span className="font-semibold">--</span>
            )}
          </div>
        ),
      }),
    ];
  }, [columnHelper, t, nameTranslation]);

  return { columns };
}
